import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api";
import { useSession } from "../../state";

type Filters = {
  from?: string; to?: string;
  country?: string; device?: string; browser?: string;
  event?: string; referrer?: string; path?: string;
  signed_in?: boolean;
};

type Summary = {
  totals?: {
    events: number; sessions: number; users: number;
    last_hour_events: number; last_hour_sessions: number; last_5min_sessions: number;
  };
  funnel?: { visitors: number; signups: number; submitters: number; purchasers: number };
  pageviews_daily?: Record<string, number>;
  events_daily?: Record<string, number>;
  hourly?: Record<string, number>;        // "dow_hour" -> count
  entry_pages?: Record<string, number>;
  exit_pages?: Record<string, number>;
  by_event?: Record<string, number>;
  by_path?: Record<string, number>;
  by_country?: Record<string, number>;
  by_device?: Record<string, number>;
  by_browser?: Record<string, number>;
  by_referrer?: Record<string, number>;
};

const DOW = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function buildQuery(days: number, f: Filters): string {
  const p = new URLSearchParams();
  if (f.from && f.to) {
    p.set("from", f.from);
    p.set("to", f.to);
  } else {
    p.set("days", String(days));
  }
  if (f.country)  p.set("country", f.country);
  if (f.device)   p.set("device", f.device);
  if (f.browser)  p.set("browser", f.browser);
  if (f.event)    p.set("event", f.event);
  if (f.path)     p.set("path", f.path);
  if (f.referrer) p.set("referrer", f.referrer);
  if (f.signed_in) p.set("signed_in", "true");
  return p.toString();
}

export function AdminAnalytics() {
  const { token } = useSession();
  const [days, setDays] = useState(7);
  const [filters, setFilters] = useState<Filters>({});
  const [data, setData] = useState<Summary | null>(null);

  const customRange = !!(filters.from && filters.to);

  useEffect(() => {
    let alive = true;
    (async () => {
      const t = (await token())!;
      const qs = buildQuery(days, filters);
      const d = await apiFetch(`/admin/analytics/summary?${qs}`, t);
      if (alive) setData(d);
    })();
    return () => { alive = false; };
  }, [days, filters]);

  const setFilter = (k: keyof Filters, v: string | boolean | undefined) => {
    setFilters((f) => {
      const n: any = { ...f };
      if (v === undefined || v === "" || v === false) delete n[k];
      else n[k] = v;
      return n;
    });
  };
  const clearAllFilters = () => setFilters({});

  if (!data) return <p className="text-ink-soft text-sm">loading…</p>;
  const totals = data.totals || ({} as any);
  const funnel = data.funnel || ({} as any);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-ink-soft">window:</label>
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => { setDays(d); setFilter("from", undefined); setFilter("to", undefined); }}
              className={`text-xs px-3 py-1 rounded-full ${!customRange && days === d ? "bg-ink text-cream" : "bg-cream-dark text-ink-soft"}`}
            >
              {d}d
            </button>
          ))}
          <span className="text-xs text-ink-mute mx-1">·</span>
          <input
            type="date"
            className="text-xs px-2 py-1 rounded-full bg-cream-dark border-0"
            value={filters.from || ""}
            onChange={(e) => setFilter("from", e.target.value)}
          />
          <span className="text-xs text-ink-mute">→</span>
          <input
            type="date"
            className="text-xs px-2 py-1 rounded-full bg-cream-dark border-0"
            value={filters.to || ""}
            onChange={(e) => setFilter("to", e.target.value)}
          />
          <label className="text-xs text-ink-soft flex items-center gap-1 ml-2">
            <input
              type="checkbox"
              className="size-3 accent-coral"
              checked={!!filters.signed_in}
              onChange={(e) => setFilter("signed_in", e.target.checked)}
            />
            signed-in only
          </label>
        </div>
        <div className="text-xs text-ink-mute">
          <b className="text-coral">●</b>{" "}
          {totals.last_5min_sessions ?? 0} active in the last 5 min ·{" "}
          {totals.last_hour_events ?? 0} events / {totals.last_hour_sessions ?? 0} sessions in the last hour
        </div>
      </div>

      <FilterChips filters={filters} setFilter={setFilter} clear={clearAllFilters} />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="events" value={totals.events ?? 0} />
        <Stat label="sessions" value={totals.sessions ?? 0} />
        <Stat label="signed-in users" value={totals.users ?? 0} />
      </div>

      <Section title="funnel (distinct sessions in window)">
        <div className="grid grid-cols-4 gap-2">
          <FunnelStep label="visitors"   value={funnel.visitors ?? 0} prev={null} />
          <FunnelStep label="signed in"  value={funnel.signups ?? 0} prev={funnel.visitors ?? 0} />
          <FunnelStep label="submitted"  value={funnel.submitters ?? 0} prev={funnel.signups ?? 0} />
          <FunnelStep label="purchased"  value={funnel.purchasers ?? 0} prev={funnel.submitters ?? 0} />
        </div>
      </Section>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Section title={filters.event ? `${filters.event} per day` : "page views by day"}>
          <DailyChart data={data.pageviews_daily} />
        </Section>
        <Section title="all matching events by day">
          <DailyChart data={data.events_daily} colorClass="bg-teal" />
        </Section>
      </div>

      <Section title="activity heatmap (hour × day, page views)">
        <Heatmap data={data.hourly} />
      </Section>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Section title="entry pages (first in session)">
          <Bars data={data.entry_pages} onClick={(k) => setFilter("path", k)} />
        </Section>
        <Section title="exit pages (last in session)">
          <Bars data={data.exit_pages} onClick={(k) => setFilter("path", k)} />
        </Section>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Section title="top pages"><Bars data={data.by_path} onClick={(k) => setFilter("path", k)} /></Section>
        <Section title="top events"><Bars data={data.by_event} onClick={(k) => setFilter("event", k)} /></Section>
        <Section title="referrers"><Bars data={data.by_referrer} onClick={(k) => setFilter("referrer", k)} /></Section>
        <Section title="countries"><Bars data={data.by_country} onClick={(k) => setFilter("country", k)} /></Section>
        <Section title="devices"><Bars data={data.by_device} onClick={(k) => setFilter("device", k)} /></Section>
        <Section title="browsers"><Bars data={data.by_browser} onClick={(k) => setFilter("browser", k)} /></Section>
      </div>
    </div>
  );
}

function FilterChips({ filters, setFilter, clear }: { filters: Filters; setFilter: any; clear: () => void }) {
  const items: { k: keyof Filters; label: string }[] = [
    { k: "country", label: "country" }, { k: "device", label: "device" },
    { k: "browser", label: "browser" }, { k: "event", label: "event" },
    { k: "path", label: "path" }, { k: "referrer", label: "referrer" },
  ];
  const visible = items.filter(({ k }) => !!filters[k]);
  if (visible.length === 0 && !filters.signed_in) return <div className="h-3" />;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs">
      {visible.map(({ k, label }) => (
        <span key={k} className="inline-flex items-center gap-1 bg-coral/15 text-coral-dark px-2 py-1 rounded-full">
          {label}: <b>{String(filters[k])}</b>
          <button onClick={() => setFilter(k, undefined)} className="opacity-60 hover:opacity-100">×</button>
        </span>
      ))}
      {filters.signed_in && (
        <span className="inline-flex items-center gap-1 bg-coral/15 text-coral-dark px-2 py-1 rounded-full">
          signed-in only
          <button onClick={() => setFilter("signed_in", false)} className="opacity-60 hover:opacity-100">×</button>
        </span>
      )}
      <button onClick={clear} className="text-ink-mute hover:text-ink underline ml-1">clear all</button>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-cream-dark/60 rounded-2xl p-4">
      <div className="text-xs text-ink-mute uppercase tracking-wider">{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function FunnelStep({ label, value, prev }: { label: string; value: number; prev: number | null }) {
  const pct = prev && prev > 0 ? Math.round((value / prev) * 100) : null;
  return (
    <div className="bg-cream rounded-2xl p-3 border border-ink/5">
      <div className="text-[10px] uppercase tracking-wider text-ink-mute">{label}</div>
      <div className="font-display font-black text-2xl leading-tight">{value}</div>
      {pct !== null && (
        <div className={`text-[10px] mt-1 ${pct >= 30 ? "text-teal" : pct >= 10 ? "text-amber" : "text-coral-dark"}`}>
          {pct}% of previous
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-cream-dark/30 rounded-2xl p-4 mt-4">
      <h3 className="text-xs uppercase tracking-wider text-ink-mute mb-2">{title}</h3>
      {children}
    </div>
  );
}

function DailyChart({ data, colorClass = "bg-coral" }: { data?: Record<string, number>; colorClass?: string }) {
  const entries = Object.entries(data || {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return <p className="text-ink-mute text-xs">no data</p>;
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="flex flex-col gap-1">
      {/* count labels above each bar */}
      <div className="flex gap-1">
        {entries.map(([d, c]) => (
          <span key={d} className={`flex-1 text-[10px] text-center font-medium ${c === 0 ? "text-ink-mute" : "text-ink"}`}>
            {c}
          </span>
        ))}
      </div>
      {/* fixed-height bars row so the per-bar height percentages resolve */}
      <div className="flex items-end gap-1 h-24">
        {entries.map(([d, c]) => (
          <div
            key={d}
            className={`flex-1 ${colorClass} rounded-sm`}
            style={{ height: `${Math.max(2, (c / max) * 100)}%`, opacity: c === 0 ? 0.15 : 1 }}
            title={`${d}: ${c}`}
          />
        ))}
      </div>
      <div className="flex gap-1">
        {entries.map(([d]) => (
          <span key={d} className="flex-1 text-[10px] text-ink-mute text-center truncate">{d.slice(5)}</span>
        ))}
      </div>
    </div>
  );
}

function Heatmap({ data }: { data?: Record<string, number> }) {
  const grid = useMemo(() => {
    const m: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    Object.entries(data || {}).forEach(([k, v]) => {
      const [dow, hr] = k.split("_").map(Number);
      if (dow >= 0 && dow < 7 && hr >= 0 && hr < 24) m[dow][hr] = v;
    });
    return m;
  }, [data]);
  const max = Math.max(1, ...grid.flat());
  if (max === 1 && grid.flat().every((v) => v === 0)) return <p className="text-ink-mute text-xs">no data</p>;
  return (
    <div className="flex flex-col gap-0.5 text-[10px] text-ink-mute">
      <div className="flex gap-0.5 ml-8">
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="w-3 text-center">{h % 6 === 0 ? h : ""}</div>
        ))}
      </div>
      {DOW.map((d, dow) => (
        <div key={d} className="flex items-center gap-0.5">
          <div className="w-8">{d}</div>
          {grid[dow].map((c, hr) => (
            <div
              key={hr}
              className="w-3 h-3 rounded-sm"
              style={{
                backgroundColor: c === 0 ? "rgba(42,26,20,.06)" : "#f87060",
                opacity: c === 0 ? 1 : 0.2 + 0.8 * (c / max),
              }}
              title={`${d} ${hr}:00 — ${c}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function Bars({ data, onClick }: { data?: Record<string, number>; onClick?: (k: string) => void }) {
  const entries = Object.entries(data || {}).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return <p className="text-ink-mute text-xs">no data</p>;
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <ul className="text-xs space-y-1">
      {entries.map(([k, v]) => (
        <li key={k} className="flex items-center gap-2">
          {onClick ? (
            <button
              onClick={() => onClick(k)}
              className="flex-1 truncate text-left hover:text-coral-dark hover:underline"
              title={`filter by ${k}`}
            >
              {k}
            </button>
          ) : (
            <span className="flex-1 truncate" title={k}>{k}</span>
          )}
          <span className="bg-coral/50 h-1.5 rounded-full" style={{ width: `${(v / max) * 80}px` }} />
          <span className="w-10 text-right text-ink-soft">{v}</span>
        </li>
      ))}
    </ul>
  );
}
